/**
 * The ZIP state every ZIP Code on a page shares: the committed ZIP, its RSN
 * fee and its counties. A subscribe/getSnapshot store, so React reads it with
 * `useSyncExternalStore` (see `molecules/zip-code/use-zip-session.client.ts`) and two
 * instances on one page can never disagree.
 *
 * It replaces the legacy `ApplicationContextProvider`, which v3 does not
 * mount: it hydrates from `sessionStorage` and the cookies, fetches the fee
 * when there is none on load, and writes the same cookies and storage keys the
 * old pages read back.
 *
 * It also reconciles with other documents on this origin — an iframe, another tab —
 * which share those cookies and that storage but not this memory; see `resync`.
 */

import { COOKIE_CURRENT_ZIP, COOKIE_RSN_FEE, getCookie, getCurrentZipCookie, setCurrentZipCookie, writeCountyCookie, writeRsnFeeCookie } from "./zip-cookies";
import { countyKey, DEFAULT_CHANNEL_LOOKUP_PATH, toZipCounty, type ZipCounty } from "./county-lookup";
import { DEFAULT_OFFERS_PATH, fetchRsnFee, formatRsnFee, isValidRsnFee } from "./rsn-fee";
import { isValidFullZip } from "./zip-validator";

export const STORAGE_RSN_FEE_DATA = "rsnFeeData";
export const STORAGE_LOCALS_ELIGIBLE = "isLocalsEligible";

export interface ZipStoreConfig {
  /** Channel lookup service base, trailing slash included. */
  channelLookupPath?: string;
  /** Offers endpoint the RSN fee is read from. */
  offersPath?: string;
  /** Prefix for `/api/local-channels` — the app's base path, "" when it has none. */
  localChannelsBasePath?: string;
}

export interface ZipStoreState {
  /** The committed ZIP. "" until the client resolves one, so server HTML never carries a ZIP. */
  zip: string;
  /** The fee for `feeZip`, formatted "13.99"; "" when unknown or the fetch failed. */
  rsnFee: string;
  /** The ZIP `rsnFee` was fetched for. A fee only counts for the committed ZIP when these match. */
  feeZip: string;
  feeLoading: boolean;
  feeError: boolean;
  /** Counties of the committed ZIP. */
  counties: ZipCounty[];
  /**
   * The county a successful Update committed, so every instance on the page shows the
   * same one. The DMA cookies cannot name it, so it travels twice: as
   * `committedCountyKey` in the record below, and as the `currentCounty` cookie, which
   * is what reaches another tab. Only meaningful beside the ZIP in its own `zipcode`.
   */
  committedCounty: ZipCounty | null;
  /**
   * The `rsnFee` cookie's figure — the fee an older page, or an earlier visit in
   * ANOTHER TAB, already resolved for this visitor. "" when there is no usable cookie.
   *
   * ⚠ STRICTLY A FALLBACK, and never a substitute for `rsnFee`. The cookie carries no
   * ZIP of its own, so it can only be trusted while this document has committed
   * nothing: `rsnFee`/`feeZip` outrank it, and it is dropped the moment a ZIP is
   * committed. Read it as `rsnFee || cookieRsnFee`, never the other way round.
   *
   * It exists because `hydrate` cannot reach it: that reads `sessionStorage` first and
   * returns when the area is empty (a WAT-11988 contract), and `sessionStorage` is
   * per-TAB while the cookies are per-BROWSER. A returning visitor in a second tab has
   * the cookies and no storage. See `seedCookieFee`.
   */
  cookieRsnFee: string;
}

interface FeeCache {
  zipcode: string;
  rsnFee: string;
  countyOptions?: ZipCounty[];
  /* The county the reader chose, as `countyKey`. Two counties can share a DMA, so the
     DMA cookies cannot name it and it would otherwise be lost on reload (WAT-11318). */
  committedCountyKey?: string;
}

const EMPTY_STATE: ZipStoreState = {
  zip: "",
  rsnFee: "",
  feeZip: "",
  feeLoading: false,
  feeError: false,
  counties: [],
  committedCounty: null,
  cookieRsnFee: ""
};

function readStorage(key: string): string | null {
  try {
    return typeof window !== "undefined" ? window.sessionStorage?.getItem(key) ?? null : null;
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string | null): void {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) {
      return;
    }
    if (value === null) {
      window.sessionStorage.removeItem(key);
    } else {
      window.sessionStorage.setItem(key, value);
    }
  } catch {
    // Storage blocked or full: the cookies still carry the ZIP and fee.
  }
}

export function createZipStore(initialConfig: ZipStoreConfig = {}) {
  let state: ZipStoreState = EMPTY_STATE;
  let cache: FeeCache | null = null;
  let config: Required<ZipStoreConfig> = {
    channelLookupPath: DEFAULT_CHANNEL_LOOKUP_PATH,
    offersPath: DEFAULT_OFFERS_PATH,
    localChannelsBasePath: "",
    ...stripUndefined(initialConfig)
  };
  let initialised = false;
  let feeSeq = 0;
  /** Removes the cross-document listeners; set only while someone is subscribed. */
  let release: (() => void) | undefined;
  const listeners = new Set<() => void>();

  function setState(patch: Partial<ZipStoreState>) {
    state = {
      ...state,
      ...patch
    };
    // Snapshot first, notify after: a listener that reads the store sees the new state.
    listeners.forEach(listener => listener());
  }

  function persistCache(next: FeeCache | null) {
    // The callers rebuild the record, so a choice already stored for this ZIP is carried over.
    const kept = next && next.committedCountyKey === undefined && cache?.zipcode === next.zipcode ?
      cache.committedCountyKey :
      next?.committedCountyKey;
    cache = next ? {
      ...next,
      committedCountyKey: kept
    } : null;
    writeStorage(
      STORAGE_RSN_FEE_DATA,
      cache ? JSON.stringify({
        ...cache,
        countyOptions: cache.countyOptions?.map(county => ({ ...county }))
      }) : null
    );
  }

  /* The counties kept alongside a fee entry only when they are for the same ZIP. */
  function countiesFor(zip: string): ZipCounty[] | undefined {
    return cache && cache.zipcode === zip ? cache.countyOptions : undefined;
  }

  /**
   * Restores the ZIP, fee and counties the old pages left in `sessionStorage`
   * (only when the `currentZipcode` cookie agrees with them), else from the two
   * cookies when both exist. Absent storage does nothing.
   */
  function hydrate() {
    try {
      const raw = readStorage(STORAGE_RSN_FEE_DATA);
      if (!raw) {
        return;
      }
      const stored = JSON.parse(raw);
      /* Storage is untrusted input (the old pages write it too): only the exact
         shape is used, so a malformed entry cannot throw below or poison the cache. */
      if (
        !stored || typeof stored !== "object" ||
        typeof stored.zipcode !== "string" || typeof stored.rsnFee !== "string" ||
        (stored.countyOptions !== undefined && !Array.isArray(stored.countyOptions))
      ) {
        return;
      }
      const zipCookie = getCookie(COOKIE_CURRENT_ZIP);
      const feeCookie = getCookie(COOKIE_RSN_FEE);
      if (zipCookie && zipCookie === stored.zipcode) {
        cache = stored;
        const patch: Partial<ZipStoreState> = {};
        if (isValidFullZip(stored.zipcode) && isValidRsnFee(stored.rsnFee)) {
          patch.zip = stored.zipcode;
          patch.rsnFee = stored.rsnFee;
          patch.feeZip = stored.zipcode;
          /* A seeded cookie fee is spent the moment a ZIP is known, as in `commitZip`.
             `rsnFee` outranks it anyway, so this is the INVARIANT being kept true
             rather than a visible fix: nothing should be able to read a ZIP-less
             fallback beside a committed ZIP. */
          patch.cookieRsnFee = "";
        }
        if (Array.isArray(stored.countyOptions)) {
          try {
            patch.counties = stored.countyOptions.map(toZipCounty);
          } catch {
            // Unreadable counties are looked up again rather than trusted.
          }
        }
        /* A stored choice is restored only by matching a county still on offer, so a
           stale key simply falls back to the DMA match rather than seating a county
           that is no longer in the list. */
        if (typeof stored.committedCountyKey === "string" && patch.counties) {
          patch.committedCounty = patch.counties.find(county => countyKey(county) === stored.committedCountyKey) ?? null;
        }
        setState(patch);
      } else if (zipCookie && feeCookie) {
        cache = {
          zipcode: zipCookie,
          rsnFee: feeCookie
        };
        if (isValidFullZip(zipCookie) && isValidRsnFee(feeCookie)) {
          setState({
            zip: zipCookie,
            rsnFee: feeCookie,
            feeZip: zipCookie,
            // Same invariant as the branch above: no ZIP-less fallback beside a ZIP.
            cookieRsnFee: ""
          });
        }
      }
    } catch {
      cache = null;
    }
  }

  /**
   * Has this ZIP already been priced, and come back with nothing? Only a stored record
   * can say. `commitZip` writes `currentZipcode` before `loadFee` resolves, so after a
   * failed fetch both cookies name the new ZIP with the PREVIOUS ZIP's price.
   *
   * ⚠ A record is matched by EXACT ZIP (`recordFor`), so `pricedEmptyFor("")` is always
   * false — there is no record whose `zipcode` is "". Anything testing this against an
   * uncommitted `state.zip` is therefore a no-op; pass the ZIP COOKIE instead.
   */
  function pricedEmptyFor(zip: string): boolean {
    const record = recordFor(zip);
    return record !== null && !isValidRsnFee(record.rsnFee);
  }

  /**
   * Reads the returning visitor's fee out of the `rsnFee` cookie into
   * `state.cookieRsnFee`. Called from `subscribe` on every 0→1 subscriber transition
   * — so it is client-only by construction (React never subscribes during SSR), it
   * runs for every `createZipStore()` without a caller remembering to, and it needs no
   * ZIP Code molecule on the page. That last point is the whole reason it is not in
   * `init`: `init` is reached only through `useZipSession` → `ZipCodeConnected`, so a
   * section that draws no ZIP never starts the store at all, and its plan cards still
   * need the visitor's fee.
   *
   * ⚠ NOT ONCE PER PAGE. A `seeded` latch was tried and removed: `zipStore` is a
   * module singleton that outlives any one route, so on an App Router navigation — no
   * reload, same JS context — a latch would hand a freshly mounted leaf a figure read
   * before another tab rewrote the cookies. Re-reading on each transition costs two
   * `document.cookie` parses per transition and cannot go stale. The `state.zip` guard
   * below makes a Strict Mode re-subscribe a no-op anyway.
   *
   * ⚠ WRITES `cookieRsnFee` AND NOTHING ELSE — no `zip`, no `rsnFee`, no fetch. Three
   * other paths already decide whether this same cookie may become the REAL fee
   * (`hydrate`, `applyCounties`, and `zip-session`'s syncload); this one must not make
   * that decision for them, or the WAT-11988 contract that `hydrate` ignores cookies
   * without `sessionStorage` is quietly reversed.
   */
  function seedCookieFee() {
    /* `subscribe` is a client-only caller, so this is belt-and-braces for a direct
       `createZipStore()` in a node-environment spec rather than a reachable branch. */
    if (typeof document === "undefined") {
      return;
    }
    /* This document has committed a ZIP already, so either `rsnFee` answers for it or
       it was priced and came back empty. Both outrank a cookie that names no ZIP. */
    if (state.zip) {
      return;
    }
    const fromCookie = getCookie(COOKIE_RSN_FEE);
    /* ⚠ TESTED AGAINST THE ZIP COOKIE, not `state.zip`. The cookie carries no ZIP, so
       after a failed fetch for B it still holds A's fee while both cookies name B
       (Z-138). The stored record is the proof, and unlike `state.zip` it survives a
       reload IN THE SAME TAB.
       ⚠ Its reach stops at the tab boundary: `sessionStorage` is per-tab, so in a
       SECOND tab `recordFor` is always null and this test cannot fire. A failed fetch
       in tab 1 can therefore still show its previous fee in a fresh tab 2.
       `zip-session`'s syncload (`:382`) has the same blind spot; closing it needs the
       record to move to a cookie, which is a separate change.
       Even so this is stricter than the per-leaf read it replaces: that one tested
       `state.zip`, which on a page without a ZIP molecule is always "" — and
       `recordFor` matches by EXACT ZIP, so its guard never fired at all. */
    const next = isValidRsnFee(fromCookie) && !pricedEmptyFor(getCookie(COOKIE_CURRENT_ZIP) ?? "") ?
      (fromCookie as string) :
      "";
    /* Reference stability: an unchanged value must not allocate a new state object,
       or `getSnapshot() === getServerSnapshot()` stops holding before anything moves. */
    if (next !== state.cookieRsnFee) {
      setState({ cookieRsnFee: next });
    }
  }

  async function loadFee(zip: string) {
    feeSeq += 1;
    const token = feeSeq;
    setState({
      feeLoading: true,
      feeError: false
    });

    if (cache && cache.zipcode === zip && isValidRsnFee(cache.rsnFee)) {
      setState({
        rsnFee: cache.rsnFee,
        feeZip: zip,
        feeLoading: false
      });
      writeRsnFeeCookie(cache.rsnFee);
      return;
    }

    let formatted = "";
    try {
      const fee = await fetchRsnFee(zip, { offersPath: config.offersPath });
      // Latest wins: a ZIP committed while this was in flight owns the fee state.
      if (token !== feeSeq) {
        return;
      }
      if (!isValidRsnFee(fee)) {
        throw new Error("Invalid RSN Fee");
      }
      formatted = formatRsnFee(fee);
    } catch {
      if (token !== feeSeq) {
        return;
      }
      // The cookie is NOT cleared, so consumers keep the previous ZIP's price.
      setState({
        rsnFee: "",
        feeZip: zip,
        feeLoading: false,
        feeError: true
      });
      persistCache({
        zipcode: zip,
        rsnFee: "",
        countyOptions: countiesFor(zip)
      });
      return;
    }
    setState({
      rsnFee: formatted,
      feeZip: zip,
      feeLoading: false
    });
    writeRsnFeeCookie(formatted);
    persistCache({
      zipcode: zip,
      rsnFee: formatted,
      countyOptions: countiesFor(zip)
    });
  }

  function configure(next: ZipStoreConfig) {
    config = {
      ...config,
      ...stripUndefined(next)
    };
  }

  /** Caches the counties against a fee for the SAME ZIP only, and returns the state patch. */
  function applyCounties(counties: ZipCounty[]): Partial<ZipStoreState> {
    if (!counties?.length) {
      return { counties: [] };
    }
    const { zipcode } = counties[0];
    /* ⚠ The cookie fee counts only while this document has not tried to price the ZIP
       itself. `commitZip` writes the new `currentZipcode` before the fetch resolves and
       `loadFee` keeps the old price in `rsnFee` on failure, so in that window both
       cookies agree and are wrong together — and `resync` trusts records. */
    let rsnFee = cache && cache.zipcode === zipcode ? cache.rsnFee : "";
    if (!rsnFee && state.feeZip === zipcode) {
      rsnFee = state.rsnFee;
    }
    const pricingThisZip = state.feeLoading || state.feeZip === zipcode;
    /* The same window survives a RELOAD, where `feeZip` is empty again. A record naming
       this ZIP with no fee is what `loadFee` leaves on failure, and the only proof left
       that the two agreeing cookies agree on the PREVIOUS ZIP's price. */
    const pricedEmpty = cache !== null && cache.zipcode === zipcode && !isValidRsnFee(cache.rsnFee);
    const cookieFee = getCookie(COOKIE_RSN_FEE);
    if (!rsnFee && !pricingThisZip && !pricedEmpty && cookieFee && getCookie(COOKIE_CURRENT_ZIP) === zipcode) {
      rsnFee = cookieFee;
    }
    persistCache({
      zipcode,
      rsnFee,
      countyOptions: counties
    });
    return { counties };
  }

  function commitZip(zip: string, counties: ZipCounty[] | undefined, fetchFee: boolean, committedCounty?: ZipCounty) {
    const patch: Partial<ZipStoreState> = counties ? applyCounties(counties) : {};
    const zipChanged = isValidFullZip(zip) && zip !== state.zip;
    if (zipChanged) {
      patch.zip = zip;
      /* ⚠ Z-138. The seeded cookie names no ZIP, so the moment this document commits
         one it is worthless — and if the fetch then FAILS, `rsnFee` stays "" and a
         surviving fallback would show the PREVIOUS ZIP's price indefinitely. Dropping
         it here covers that branch too: `loadFee` is reached only from this line and
         from `resync`, and both set the ZIP. */
      patch.cookieRsnFee = "";
    }
    // Same notification as the ZIP, so no instance sees the new ZIP beside the old county.
    if (committedCounty) {
      patch.committedCounty = committedCounty;
      // Storage for this tab, cookie for the others: storage dies with the tab.
      writeCountyCookie(countyKey(committedCounty));
      if (cache && cache.zipcode === committedCounty.zipcode) {
        persistCache({
          ...cache,
          committedCountyKey: countyKey(committedCounty)
        });
      }
    } else if (zipChanged) {
      patch.committedCounty = null;
    }
    if (Object.keys(patch).length > 0) {
      setState(patch);
    }
    if (zipChanged) {
      setCurrentZipCookie(zip);
      if (fetchFee) {
        // loadFee catches its own errors; the catch here is the last line against an unhandled rejection.
        loadFee(zip).catch(() => undefined);
      }
    }
  }

  function updateZip(zip: string, fetchFee: boolean) {
    commitZip(zip, undefined, fetchFee);
  }

  /** The stored record, but only when it names `zip` — so a fee is never read for another ZIP. */
  function recordFor(zip: string): FeeCache | null {
    try {
      const raw = readStorage(STORAGE_RSN_FEE_DATA);
      const stored = raw ? JSON.parse(raw) : null;
      if (
        !stored || typeof stored !== "object" ||
        stored.zipcode !== zip || typeof stored.rsnFee !== "string" ||
        (stored.countyOptions !== undefined && !Array.isArray(stored.countyOptions))
      ) {
        return null;
      }
      return stored;
    } catch {
      return null;
    }
  }

  /** `countyKey`, but "" for no county, so the two are comparable. */
  function keyOf(county: ZipCounty | null): string {
    return county ? countyKey(county) : "";
  }

  function sameCounties(left: ZipCounty[], right: ZipCounty[]): boolean {
    return left.length === right.length &&
      left.every((county, index) => countyKey(county) === countyKey(right[index]));
  }

  /**
   * Reconciles the store with a ZIP another document committed. Not `hydrate`: that
   * runs once against an empty store and only ever ADDS, so the previous ZIP's
   * counties would stand beside the new ZIP. Not `init`, which would commit 75001.
   */
  function resync() {
    if (!initialised) {
      return;
    }
    const zip = getCookie(COOKIE_CURRENT_ZIP);
    if (!zip || !isValidFullZip(zip)) {
      return;
    }
    const zipChanged = zip !== state.zip;
    const record = recordFor(zip);
    /* Only a record pairs a fee with its ZIP. The `rsnFee` cookie carries no ZIP, and
       a failed fetch leaves the previous ZIP's price in it on purpose (`loadFee`). */
    const fee = record && isValidRsnFee(record.rsnFee) ? record.rsnFee : "";

    let stored: ZipCounty[] | null = null;
    try {
      stored = record?.countyOptions ? record.countyOptions.map(toZipCounty) : null;
    } catch {
      // Unreadable counties are looked up again rather than trusted.
    }
    /* Counties belong to a ZIP. The other document may simply not have persisted
       any — keep ours while the ZIP stands, drop them the moment it changes. */
    const counties = stored ?? (zipChanged ? [] : state.counties);
    const chosen = record?.committedCountyKey ?
      counties.find(county => countyKey(county) === record.committedCountyKey) ?? null :
      zipChanged ? null : state.committedCounty;

    /* `loadFee` sets `feeZip` only once it RESOLVES, so a running fetch leaves it
       empty. Counting that as settled is what stops a second event mid-flight from
       cancelling the request through `feeSeq` and starting it again. */
    const feeSettled = fee ?
      fee === state.rsnFee && zip === state.feeZip :
      state.feeLoading || zip === state.feeZip;
    if (
      !zipChanged && feeSettled &&
      sameCounties(counties, state.counties) && keyOf(chosen) === keyOf(state.committedCounty)
    ) {
      return;
    }

    // Latest wins: a fee still in flight was asked for before this, so it loses.
    feeSeq += 1;
    if (record) {
      cache = record;
    } else if (zipChanged) {
      cache = null;
    }
    // One notification, so no instance sees the new ZIP beside the old county.
    setState({
      zip,
      rsnFee: fee,
      feeZip: fee ? zip : "",
      feeLoading: false,
      feeError: false,
      counties,
      committedCounty: chosen,
      // The other document committed a ZIP, so the ZIP-less cookie fee is spent — as in `commitZip`.
      cookieRsnFee: ""
    });
    if (!fee) {
      loadFee(zip).catch(() => undefined);
    }
  }

  /**
   * `storage` covers the same-origin iframe, which shares this tab's storage area.
   * `visibilitychange` covers what it cannot: another tab, whose `sessionStorage` is
   * its own so only the cookies crossed, and the paths that write no storage at all.
   */
  function onStorage(event: StorageEvent) {
    if (event.storageArea !== window.sessionStorage) {
      return;
    }
    // `null` is a `clear()`, which drops our key with the rest.
    if (event.key !== null && event.key !== STORAGE_RSN_FEE_DATA) {
      return;
    }
    resync();
  }

  function onVisible() {
    if (document.visibilityState === "visible") {
      resync();
    }
  }

  function attach() {
    if (typeof window === "undefined" || release) {
      return;
    }
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisible);
    release = () => {
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisible);
    };
    /* Nothing was listening until now and `init` is one-shot, so a commit made while
       no ZIP Code was mounted would never be read. Only with an answer already here:
       with none, `zip-session`'s syncload is still deciding whether the `rsnFee`
       cookie may be adopted, and resyncing first prices that ZIP behind its back. */
    if (state.zip) {
      resync();
    }
  }

  return {
    /**
     * The subscriber count is also the cross-document listeners' lifetime, so a page with
     * no ZIP Code mounted holds nothing and a Strict Mode double-mount nets to one.
     */
    subscribe(listener: () => void) {
      const first = listeners.size === 0;
      /* ⚠ BEFORE the add, unlike `attach` below. The seed must notify NOBODY: the set
         is still empty, so `setState` reaches no listener, and React picks the new
         snapshot up anyway in each leaf's own post-commit consistency check. Seeding
         after the add would push a notification into a listener that has not finished
         mounting — which `zip-store.spec.ts`'s "a subscriber that throws during
         hydration cannot break init" exists to catch. */
      if (first) {
        seedCookieFee();
      }
      listeners.add(listener);
      // After the add, so `attach`'s catch-up resync notifies this subscriber too.
      if (first) {
        attach();
      }
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
          release?.();
          release = undefined;
        }
      };
    },
    getSnapshot: () => state,
    /** Always the empty state, so hydration matches the server's HTML. */
    getServerSnapshot: () => EMPTY_STATE,
    getConfig: () => config,
    /** Merges endpoint overrides; a missing value keeps what is already set. */
    configure,

    /**
     * Once per page: restore state from storage and cookies, then — with no
     * `rsnFee` cookie — commit the best ZIP and fetch its fee, so a first
     * visit is priced for the visitor rather than the default ZIP. That commit
     * writes `currentZipcode`, 75001 included, as the production bootstrap does.
     */
    init(next?: ZipStoreConfig) {
      if (next) {
        configure(next);
      }
      if (initialised) {
        return;
      }
      initialised = true;
      hydrate();
      if (!getCookie(COOKIE_RSN_FEE)) {
        updateZip(getCurrentZipCookie(), true);
      }
    },

    /**
     * Commits a ZIP: writes the cookie and, when asked, fetches its fee. A ZIP
     * equal to the committed one does nothing — no second request.
     */
    updateZip,

    /**
     * Adopts a ZIP committed by another document on this origin. Called for you on
     * a `storage` event and when the page comes back to the front; public so a host
     * that knows a commit just happened — closing an iframe it owns — can ask.
     */
    resync,

    /**
     * Commits a ZIP and its counties in ONE notification, so another instance
     * never sees the new ZIP beside the old counties and looks the ZIP up again.
     * `committedCounty`, when given, is the reader's choice, shared with the
     * other instances in that same notification.
     */
    commitZip,

    /**
     * Has this ZIP already been priced, and come back with nothing? Only a stored record
     * can say. `commitZip` writes `currentZipcode` before `loadFee` resolves, so after a
     * failed fetch both cookies name the new ZIP with the PREVIOUS ZIP's price.
     */
    pricedEmpty: pricedEmptyFor,

    /** Sets a known fee and the ZIP it belongs to, as when a cookie already holds it. */
    updateRsnFee(fee: string, zip: string = state.zip) {
      if (fee !== state.rsnFee || zip !== state.feeZip) {
        setState({
          rsnFee: fee,
          feeZip: zip
        });
        writeRsnFeeCookie(fee);
      }
    },

    /**
     * Stores the counties of a ZIP. ⚠ The cache pairs them with a fee only for
     * the SAME ZIP — production kept the previous ZIP's fee, so a later hit
     * served the wrong price. The `rsnFee` cookie counts only while the
     * `currentZipcode` cookie names this ZIP.
     */
    updateCounties(counties: ZipCounty[]) {
      setState(applyCounties(counties));
    },

    /** The key old plan cards read on mount: anything but "false" counts as eligible. */
    setLocalsEligible(eligible: boolean) {
      writeStorage(STORAGE_LOCALS_ELIGIBLE, String(eligible));
    }
  };
}

function stripUndefined<T extends object>(value: T): T {
  return Object.fromEntries(Object.entries(value || {}).filter(([, v]) => v !== undefined)) as T;
}

export type ZipStore = ReturnType<typeof createZipStore>;

/** The page-wide store every `ZipCodeConnected` shares. */
export const zipStore: ZipStore = createZipStore();
